/**
 * MONSTER TOWN — ICONS. HUD/AUDIO/TOUCH CREW (src/ui/**).
 *
 * LAW #4: NO READING REQUIRED. Every meaning in this game is carried by a
 * picture. That makes this file load-bearing, not decoration.
 *
 * Rules that keep them looking like one hand drew them:
 *  - One viewBox: 0 0 100 100. One ink colour. One stroke width family (7 for
 *    silhouette, 5 for detail), round caps and joins.
 *  - Fills come from `palette.ts`. No raw hex anywhere in this file.
 *  - Chunky and legible at 44 px on a 1024x768 iPad screen — that is the real
 *    test, not how it looks at 200 px in a design tool.
 *  - Zero external assets: these are strings of SVG, generated in code.
 *
 * An unknown icon name returns a friendly blob rather than nothing. A blank
 * square reads to a kid as "broken"; a blob reads as "a thing".
 */

import { css, type PaletteName } from '../core/palette'

const INK = css('ink')

interface Pen {
  /** Fill colour. */
  f?: PaletteName
  /** Secondary fill. */
  g?: PaletteName
}

type IconDraw = (p: Pen) => string

const S = `stroke="${INK}" stroke-linecap="round" stroke-linejoin="round"`
const OUT = `${S} stroke-width="7"`
const DET = `${S} stroke-width="5"`

function fill(p: Pen, fallback: PaletteName): string {
  return css(p.f ?? fallback)
}
function fill2(p: Pen, fallback: PaletteName): string {
  return css(p.g ?? fallback)
}

// ---------------------------------------------------------------------------
// the set
// ---------------------------------------------------------------------------

const ICONS: Record<string, IconDraw> = {
  // -- mode / core controls ------------------------------------------------
  hammer: (p) => `
    <path d="M20 78 L58 40" ${OUT} fill="none" stroke-width="12"/>
    <path d="M20 78 L58 40" stroke="${css('woodLight')}" stroke-width="6" stroke-linecap="round" fill="none"/>
    <path d="M52 18 L86 34 L72 58 L40 40 Z" fill="${fill(p, 'metal')}" ${OUT}/>
    <path d="M60 24 L78 33" stroke="${css('metalDark')}" stroke-width="4" stroke-linecap="round"/>`,

  /**
   * THE SAME HAMMER, SWITCHED ON.
   *
   * Build mode used to be legible only to someone who already knew what the ring
   * around the button meant — which is nobody who is three. This is the hammer
   * from `hammer`, stroke for stroke, with a gold head and three sparks coming
   * off it: it is unmistakably the SAME object (the r3 rule — the hammer is the
   * button's identity and may never be swapped for a different picture), and it
   * is unmistakably doing something.
   */
  hammerOn: (p) => `
    <path d="M20 78 L58 40" ${OUT} fill="none" stroke-width="12"/>
    <path d="M20 78 L58 40" stroke="${css('woodLight')}" stroke-width="6" stroke-linecap="round" fill="none"/>
    <path d="M52 18 L86 34 L72 58 L40 40 Z" fill="${fill(p, 'uiCoin')}" ${OUT}/>
    <path d="M60 24 L78 33" stroke="${css('uiCoinDark')}" stroke-width="4" stroke-linecap="round"/>
    <path d="M30 24 L22 14 M44 12 L44 2 M12 40 L2 36" ${DET} fill="none"/>`,

  /**
   * A LITTLE MONSTER, WALKING. The badge that says "right now, tapping the
   * ground moves you" — the other half of the mode toggle's state, so the
   * difference between the two modes is a picture and not only a colour.
   */
  walker: (p) => `
    <path d="M50 62 L36 86 M50 62 L66 86" ${OUT} fill="none"/>
    <path d="M50 14 C72 14 84 32 84 48 C84 66 70 76 50 76 C30 76 16 66 16 48 C16 32 28 14 50 14 Z"
          fill="${fill(p, 'monsterTeal')}" ${OUT}/>
    <circle cx="39" cy="42" r="10" fill="${css('eyeWhite')}" ${DET}/>
    <circle cx="63" cy="42" r="10" fill="${css('eyeWhite')}" ${DET}/>
    <circle cx="41" cy="44" r="4" fill="${css('pupil')}"/>
    <circle cx="65" cy="44" r="4" fill="${css('pupil')}"/>
    <path d="M8 34 L-2 32 M10 50 L0 50" ${DET} fill="none"/>`,

  /**
   * THE POINTING PAW — the whole tutorial, in one picture. Two capture reviews
   * went into it, and both defects are worth keeping written down.
   *
   * 1. IT READ AS A PADLOCK. The first version was a pointing digit the same
   *    width as the pad below it, both inside one ink outline; at 96 px on grass
   *    the join read as a keyhole. A fist has to be obviously WIDER than the
   *    finger sticking out of it, and the finger has to OVERLAP it rather than
   *    continue it — so: a wide palm, a thumb bump, and the digit drawn last so
   *    its own outline crosses the palm's top edge, the way a real finger in
   *    front of a fist does.
   *
   * 2. IT COVERED WHAT IT WAS POINTING AT. Drawn upright, a 96 px hand with its
   *    fingertip on the centre of the 96 px hammer button hid the hammer — on
   *    the beat whose whole job is teaching what the hammer is. The hand is now
   *    rotated 25 degrees about the FINGERTIP, so the tip stays exactly on the
   *    target and the palm swings clear of it.
   *
   * The tip ends up at (26, 10) in the viewBox. That is load-bearing:
   * `tutorial.ts` knows it as TIP_X / TIP_Y and lands it on the thing a kid is
   * meant to tap. Redraw this and move those.
   */
  paw: (p) => `
    <g transform="rotate(-25 26 10) translate(-24 2)">
      <rect x="22" y="42" width="60" height="50" rx="24" fill="${fill(p, 'monsterBelly')}" ${OUT}/>
      <circle cx="20" cy="64" r="13" fill="${fill(p, 'monsterBelly')}" ${OUT}/>
      <path d="M60 58 L76 58 M60 71 L76 71" ${DET} fill="none" opacity="0.45"/>
      <rect x="38" y="8" width="24" height="48" rx="12" fill="${fill(p, 'monsterBelly')}" ${OUT}/>
      <circle cx="50" cy="20" r="4" fill="${fill2(p, 'blush')}"/>
    </g>`,

  monster: (p) => `
    <path d="M28 20 L22 6 M72 20 L78 6" ${OUT} fill="none"/>
    <circle cx="22" cy="6" r="6" fill="${fill2(p, 'horn')}" ${DET}/>
    <circle cx="78" cy="6" r="6" fill="${fill2(p, 'horn')}" ${DET}/>
    <path d="M50 16 C78 16 90 38 90 58 C90 80 74 92 50 92 C26 92 10 80 10 58 C10 38 22 16 50 16 Z"
          fill="${fill(p, 'monsterTeal')}" ${OUT}/>
    <ellipse cx="50" cy="70" rx="24" ry="18" fill="${css('monsterBelly')}"/>
    <circle cx="36" cy="48" r="12" fill="${css('eyeWhite')}" ${DET}/>
    <circle cx="66" cy="48" r="12" fill="${css('eyeWhite')}" ${DET}/>
    <circle cx="38" cy="50" r="5" fill="${css('pupil')}"/>
    <circle cx="68" cy="50" r="5" fill="${css('pupil')}"/>
    <path d="M42 72 Q50 80 58 72" ${DET} fill="none"/>`,

  /**
   * "MY MONSTER" — the way back into the creator, for ever.
   *
   * The same creature as `monster`, with a sparkle on it: the hammer already
   * borrows `monster` to mean "go and play", so the control that changes what
   * your monster IS needs its own picture a 3-year-old can tell apart.
   */
  myMonster: (p) => `
    <path d="M28 22 L22 8 M72 22 L78 8" ${OUT} fill="none"/>
    <circle cx="22" cy="8" r="6" fill="${fill2(p, 'horn')}" ${DET}/>
    <circle cx="78" cy="8" r="6" fill="${fill2(p, 'horn')}" ${DET}/>
    <path d="M50 18 C76 18 88 40 88 58 C88 80 72 92 50 92 C28 92 12 80 12 58 C12 40 24 18 50 18 Z"
          fill="${fill(p, 'monsterLilac')}" ${OUT}/>
    <circle cx="38" cy="50" r="12" fill="${css('eyeWhite')}" ${DET}/>
    <circle cx="66" cy="50" r="12" fill="${css('eyeWhite')}" ${DET}/>
    <circle cx="40" cy="52" r="5" fill="${css('pupil')}"/>
    <circle cx="68" cy="52" r="5" fill="${css('pupil')}"/>
    <path d="M42 72 Q52 80 62 72" ${DET} fill="none"/>
    <path d="M82 44 L86 56 L98 60 L86 64 L82 76 L78 64 L66 60 L78 56 Z"
          fill="${css('uiCoin')}" ${DET}/>`,

  undo: () => `
    <path d="M26 42 A28 28 0 1 1 22 62" stroke="${INK}" stroke-width="11" fill="none" stroke-linecap="round"/>
    <path d="M12 20 L26 44 L50 34 Z" fill="${INK}"/>`,

  /*
   * TURN THE TOWN LEFT / RIGHT.
   *
   * These were the same thin circular arrow flipped, and at 52 px on a 1024
   * iPad a three-year-old could not tell them apart — nor tell them from the
   * undo arrow, which is also a thin ink arc. They were also pure line art
   * sitting next to a filled coin, a filled camera and a filled speaker, so the
   * camera cluster read as a different icon set from the rest of the HUD.
   *
   * Now: a chunky filled town block that STAYS PUT in both icons, and one big
   * solid wedge swinging round it. The thing that moves is the fat triangle on
   * the left or on the right — the largest, boldest shape in the glyph — and
   * the block underneath is the thing being turned. Direction reads from a
   * shape, not from the handedness of a hairline.
   */
  rotateLeft: (p) => `
    <path d="M74 40 A30 26 0 0 0 26 40" stroke="${INK}" stroke-width="12" fill="none" stroke-linecap="round"/>
    <path d="M6 34 L34 18 L34 50 Z" fill="${fill(p, 'uiAccent')}" ${OUT}/>
    <path d="M50 50 L74 62 L50 74 L26 62 Z" fill="${css('wallCream')}" ${OUT}/>
    <path d="M26 62 L26 78 L50 90 L50 74 Z" fill="${css('wood')}" ${OUT}/>
    <path d="M74 62 L74 78 L50 90 L50 74 Z" fill="${css('woodDark')}" ${OUT}/>`,

  rotateRight: (p) => `
    <path d="M26 40 A30 26 0 0 1 74 40" stroke="${INK}" stroke-width="12" fill="none" stroke-linecap="round"/>
    <path d="M94 34 L66 18 L66 50 Z" fill="${fill(p, 'uiAccent')}" ${OUT}/>
    <path d="M50 50 L74 62 L50 74 L26 62 Z" fill="${css('wallCream')}" ${OUT}/>
    <path d="M26 62 L26 78 L50 90 L50 74 Z" fill="${css('wood')}" ${OUT}/>
    <path d="M74 62 L74 78 L50 90 L50 74 Z" fill="${css('woodDark')}" ${OUT}/>`,

  /*
   * LOOK CLOSER / LOOK WIDER. Same family as the coin and the camera: a big
   * filled shape with a heavy outline, not thin line art. The lens is fat
   * enough to hold a highlight and the handle is a chunky bar, so at 52 px
   * these read as buttons rather than as diagram strokes.
   */
  zoomIn: (p) => `
    <path d="M62 62 L86 86" stroke="${INK}" stroke-width="18" fill="none" stroke-linecap="round"/>
    <circle cx="42" cy="42" r="30" fill="${fill(p, 'glass')}" stroke="${INK}" stroke-width="8"/>
    <path d="M24 28 A22 22 0 0 1 42 20" stroke="${css('flowerWhite')}" stroke-width="7"
          fill="none" stroke-linecap="round"/>
    <path d="M42 27 L42 57 M27 42 L57 42" stroke="${INK}" stroke-width="12" fill="none"
          stroke-linecap="round"/>`,

  zoomOut: (p) => `
    <path d="M62 62 L86 86" stroke="${INK}" stroke-width="18" fill="none" stroke-linecap="round"/>
    <circle cx="42" cy="42" r="30" fill="${fill(p, 'glass')}" stroke="${INK}" stroke-width="8"/>
    <path d="M24 28 A22 22 0 0 1 42 20" stroke="${css('flowerWhite')}" stroke-width="7"
          fill="none" stroke-linecap="round"/>
    <path d="M27 42 L57 42" stroke="${INK}" stroke-width="12" fill="none" stroke-linecap="round"/>`,

  coin: (p) => `
    <circle cx="50" cy="52" r="36" fill="${css('uiCoinDark')}" ${OUT}/>
    <circle cx="50" cy="46" r="34" fill="${fill(p, 'uiCoin')}" ${OUT}/>
    <path d="M50 28 L56 42 L70 42 L59 51 L63 65 L50 57 L37 65 L41 51 L30 42 L44 42 Z"
          fill="${css('uiCoinDark')}"/>`,

  bag: (p) => `
    <path d="M34 34 C34 18 66 18 66 34" ${OUT} fill="none"/>
    <path d="M22 34 L78 34 L84 88 L16 88 Z" fill="${fill(p, 'uiPrimary')}" ${OUT}/>
    <circle cx="36" cy="50" r="5" fill="${INK}"/>
    <circle cx="64" cy="50" r="5" fill="${INK}"/>`,

  camera: (p) => `
    <path d="M12 34 L34 34 L42 22 L58 22 L66 34 L88 34 L88 84 L12 84 Z"
          fill="${fill(p, 'uiAccent')}" ${OUT}/>
    <circle cx="50" cy="58" r="20" fill="${css('glass')}" ${OUT}/>
    <circle cx="50" cy="58" r="9" fill="${css('inkSoft')}"/>
    <circle cx="76" cy="44" r="4" fill="${css('uiCoin')}"/>`,

  speakerFull: (p) => `
    <path d="M14 40 L32 40 L52 22 L52 82 L32 62 L14 62 Z" fill="${fill(p, 'uiAccent')}" ${OUT}/>
    <path d="M64 36 Q74 52 64 68" ${OUT} fill="none"/>
    <path d="M78 26 Q94 52 78 78" ${OUT} fill="none"/>`,

  speakerQuiet: (p) => `
    <path d="M14 40 L32 40 L52 22 L52 82 L32 62 L14 62 Z" fill="${fill(p, 'uiAccent')}" ${OUT}/>
    <path d="M64 36 Q74 52 64 68" ${OUT} fill="none"/>`,

  speakerOff: (p) => `
    <path d="M14 40 L32 40 L52 22 L52 82 L32 62 L14 62 Z" fill="${fill(p, 'uiDisabled')}" ${OUT}/>
    <path d="M66 36 L92 66 M92 36 L66 66" ${OUT} fill="none"/>`,

  back: () => `
    <path d="M62 20 L30 50 L62 80" stroke="${INK}" stroke-width="13" fill="none"
          stroke-linecap="round" stroke-linejoin="round"/>`,

  close: () => `
    <path d="M26 26 L74 74 M74 26 L26 74" stroke="${INK}" stroke-width="13" fill="none" stroke-linecap="round"/>`,

  check: (p) => `
    <circle cx="50" cy="50" r="38" fill="${fill(p, 'uiOk')}" ${OUT}/>
    <path d="M30 52 L45 66 L72 36" stroke="${css('uiPanel')}" stroke-width="12" fill="none"
          stroke-linecap="round" stroke-linejoin="round"/>`,

  /**
   * THE ONE "YES". Bare white tick, no disc — it is drawn ON a green button
   * (`.mt-btn.go`), so the button is the disc. The app used to answer the same
   * question two different ways: the monster creator finished on a plain green
   * square with a white tick, and the slot picker finished on an ORANGE square
   * holding a green tick-in-a-circle. Same meaning, two pictures, and a
   * pre-reader has only the picture. Every confirm now wears this one.
   */
  tick: () => `
    <path d="M22 52 L42 72 L80 28" stroke="${css('uiPanel')}" stroke-width="15" fill="none"
          stroke-linecap="round" stroke-linejoin="round"/>`,

  /**
   * "WHICH ONE OF YOU IS PLAYING" — two monster faces, one behind the other.
   * The button that opens the two-slot picker used to wear a HOUSE, which to a
   * pre-reader is the picture of the place they live: they tapped it expecting
   * their front door and got a system screen. Their house is a building you
   * walk into (and now a signpost you can tap); this button is about *whose
   * town*, so it shows two monsters.
   */
  who: (p) => `
    <circle cx="34" cy="52" r="27" fill="${fill(p, 'monsterTeal')}" ${OUT}/>
    <circle cx="27" cy="48" r="6" fill="${INK}"/>
    <circle cx="42" cy="48" r="6" fill="${INK}"/>
    <path d="M26 62 Q34 69 42 62" ${DET} fill="none"/>
    <circle cx="70" cy="44" r="23" fill="${fill2(p, 'monsterLilac')}" ${OUT}/>
    <circle cx="63" cy="41" r="5" fill="${INK}"/>
    <circle cx="77" cy="41" r="5" fill="${INK}"/>
    <path d="M63 53 Q70 59 77 53" ${DET} fill="none"/>`,

  /** START SOMETHING NEW. A bare plus — the one shape that means "add" without
   *  a word, and pointedly NOT the hammer, which already means build/play. */
  plus: (p) => `
    <circle cx="50" cy="50" r="40" fill="${fill(p, 'uiOk')}" ${OUT}/>
    <path d="M50 26 L50 74 M26 50 L74 50" stroke="${css('uiPanel')}" stroke-width="14"
          fill="none" stroke-linecap="round"/>`,

  /** A little cluster of roofs: "how much you have built here". Deliberately
   *  NOT the drawer's `block` glyph, which means "a block you can place". */
  builtTown: (p) => `
    <path d="M8 88 L8 56 L30 38 L52 56 L52 88 Z" fill="${fill(p, 'wallCream')}" ${OUT}/>
    <path d="M4 58 L30 36 L56 58 Z" fill="${css('roofRed')}" ${OUT}/>
    <path d="M56 88 L56 64 L74 50 L92 64 L92 88 Z" fill="${css('wallMint')}" ${OUT}/>
    <path d="M52 66 L74 48 L96 66 Z" fill="${css('roofTeal')}" ${OUT}/>`,

  /** An empty plot with a sprout in it: "nobody lives here yet, start here". */
  emptyPlot: (p) => `
    <path d="M6 62 L50 40 L94 62 L50 84 Z" fill="${fill(p, 'grass')}" ${OUT}/>
    <path d="M50 62 L50 34" ${OUT} stroke-width="8" fill="none"/>
    <path d="M50 44 C36 44 32 34 32 28 C44 28 50 36 50 44 Z" fill="${css('leaf')}" ${DET}/>
    <path d="M50 40 C64 40 68 30 68 24 C56 24 50 32 50 40 Z" fill="${css('leafLight')}" ${DET}/>
    <path d="M22 18 L22 6 M16 12 L28 12" ${DET} stroke="${css('uiCoin')}" fill="none"/>`,

  // -- money, drawn as three UNMISTAKABLY different things ------------------
  //
  // The shop's price row is the brief's real math hook, so a disc has to mean
  // exactly one number every single time. It used to be three sizes of the same
  // gold star-coin (44 / 32 / 22 px) meaning 100 / 10 / 1, which at card size
  // read as "some coins, some slightly smaller coins" — and the row was then
  // clamped to six of any denomination, so a 70 printed six ten-coins and a kid
  // counting them got 60. Different VALUE now means a different SHAPE and a
  // different colour, and nothing is ever clamped.
  /** 100 — a purple gem. Not a coin at all. */
  gemHundred: (p) => `
    <path d="M50 8 L88 40 L50 92 L12 40 Z" fill="${fill(p, 'wallLilac')}" ${OUT}/>
    <path d="M50 8 L68 40 L50 92 L32 40 Z" fill="${css('flowerPurple')}" ${DET}/>
    <path d="M12 40 L88 40" ${DET} fill="none"/>`,
  /** 10 — the gold star coin. */
  coinTen: (p) => `
    <circle cx="50" cy="50" r="40" fill="${fill(p, 'uiCoin')}" ${OUT}/>
    <circle cx="50" cy="50" r="29" fill="${css('uiCoinDark')}" opacity="0.35"/>
    <path d="M50 26 L57 44 L76 45 L61 57 L66 76 L50 65 L34 76 L39 57 L24 45 L43 44 Z"
          fill="${css('uiPanel')}"/>`,
  /**
   * 1 — a small COPPER penny. No star, so it cannot be mistaken for a ten.
   *
   * IT WAS STEEL GREY, AND GREY MEANS OFF (fixed r3). `metal`/`metalDark` sit
   * one shade from `uiDisabled`, which is the colour this same kit uses for
   * every switched-off, unaffordable, not-yet control — so a 25-coin bench
   * printed two gold coins and five GREY pips and read as a thing that was
   * partly locked rather than a thing that costs twenty-five. Money is warm in
   * every other place it appears in this game; the penny is warm too now, and
   * the only signal left in the row is HOW MANY.
   */
  coinOne: (p) => `
    <circle cx="50" cy="50" r="34" fill="${fill(p, 'cone')}" ${OUT} stroke-width="9"/>
    <circle cx="50" cy="50" r="15" fill="${css('dirt')}" opacity="0.5"/>`,

  heart: (p) => `
    <path d="M50 84 C18 62 12 44 22 32 C32 20 46 26 50 38 C54 26 68 20 78 32 C88 44 82 62 50 84 Z"
          fill="${fill(p, 'uiHeart')}" ${OUT}/>`,

  star: (p) => `
    <path d="M50 12 L62 40 L92 44 L70 64 L76 92 L50 78 L24 92 L30 64 L8 44 L38 40 Z"
          fill="${fill(p, 'uiCoin')}" ${OUT}/>`,

  sparkle: (p) => `
    <path d="M50 14 C54 40 60 46 86 50 C60 54 54 60 50 86 C46 60 40 54 14 50 C40 46 46 40 50 14 Z"
          fill="${fill(p, 'uiCoin')}" ${OUT}/>`,

  tap: () => `
    <path d="M42 58 L42 26 A8 8 0 0 1 58 26 L58 54" ${OUT} fill="${css('monsterBelly')}"/>
    <path d="M58 48 A7 7 0 0 1 72 48 L72 68 A22 22 0 0 1 28 68 L28 56"
          fill="${css('monsterBelly')}" ${OUT}/>
    <path d="M18 20 L8 10 M50 10 L50 -2 M82 20 L92 10" ${DET} fill="none"/>`,

  arrowDown: () => `
    <path d="M50 14 L50 66" stroke="${INK}" stroke-width="13" fill="none" stroke-linecap="round"/>
    <path d="M24 56 L50 90 L76 56 Z" fill="${INK}"/>`,

  rotatePiece: (p) => `
    <path d="M50 20 L82 38 L50 56 L18 38 Z" fill="${fill(p, 'wallMint')}" ${OUT}/>
    <path d="M30 68 A24 24 0 1 0 42 52" stroke="${INK}" stroke-width="9" fill="none" stroke-linecap="round"/>
    <path d="M34 44 L44 66 L60 56 Z" fill="${INK}"/>`,

  // -- build categories (drawer tabs) --------------------------------------
  block: (p) => `
    <path d="M50 12 L86 32 L86 68 L50 88 L14 68 L14 32 Z" fill="${fill(p, 'woodLight')}" ${OUT}/>
    <path d="M14 32 L50 52 L86 32 M50 52 L50 88" ${DET} fill="none"/>`,

  floor: (p) => `
    <path d="M50 24 L90 50 L50 76 L10 50 Z" fill="${fill(p, 'floorWood')}" ${OUT}/>
    <path d="M30 37 L70 63 M70 37 L30 63" ${DET} fill="none"/>`,

  wall: (p) => `
    <path d="M22 24 L78 24 L78 84 L22 84 Z" fill="${fill(p, 'wallCream')}" ${OUT}/>
    <path d="M22 44 L78 44 M22 64 L78 64 M50 24 L50 44 M36 44 L36 64 M64 44 L64 64 M50 64 L50 84" ${DET} fill="none"/>`,

  roof: (p) => `
    <path d="M50 14 L92 52 L82 60 L50 32 L18 60 L8 52 Z" fill="${fill(p, 'roofRed')}" ${OUT}/>
    <path d="M22 62 L78 62 L78 86 L22 86 Z" fill="${css('wallCream')}" ${OUT}/>`,

  door: (p) => `
    <path d="M24 16 L76 16 L76 88 L24 88 Z" fill="${fill(p, 'doorWood')}" ${OUT}/>
    <circle cx="63" cy="54" r="6" fill="${css('uiCoin')}" ${DET}/>`,

  window: (p) => `
    <path d="M18 20 L82 20 L82 76 L18 76 Z" fill="${fill(p, 'glass')}" ${OUT}/>
    <path d="M50 20 L50 76 M18 48 L82 48" ${OUT} fill="none"/>`,

  stair: (p) => `
    <path d="M14 86 L14 62 L38 62 L38 42 L62 42 L62 22 L86 22 L86 86 Z" fill="${fill(p, 'woodLight')}" ${OUT}/>`,

  path: (p) => `
    <path d="M28 88 C28 60 72 56 72 28" stroke="${fill(p, 'path')}" stroke-width="26" fill="none" stroke-linecap="round"/>
    <path d="M28 88 C28 60 72 56 72 28" stroke="${INK}" stroke-width="6" fill="none"
          stroke-linecap="round" stroke-dasharray="2 20"/>`,

  fence: (p) => `
    <path d="M22 26 L22 88 M50 22 L50 88 M78 26 L78 88" stroke="${fill(p, 'woodLight')}" stroke-width="12"
          fill="none" stroke-linecap="round"/>
    <path d="M10 44 L90 40 M10 66 L90 62" stroke="${fill(p, 'woodLight')}" stroke-width="10" fill="none" stroke-linecap="round"/>
    <path d="M22 26 L22 88 M50 22 L50 88 M78 26 L78 88" ${DET} fill="none" opacity="0.35"/>`,

  furniture: (p) => `
    <path d="M16 50 C16 38 26 34 34 34 L66 34 C74 34 84 38 84 50 L84 72 L16 72 Z"
          fill="${fill(p, 'fabric')}" ${OUT}/>
    <path d="M32 34 L32 72 M68 34 L68 72" ${DET} fill="none"/>
    <path d="M24 72 L24 86 M76 72 L76 86" ${OUT} fill="none"/>`,

  bed: (p) => `
    <path d="M12 78 L12 46 C12 40 18 36 26 36 L74 36 C82 36 88 40 88 46 L88 78 Z"
          fill="${fill(p, 'fabricAlt')}" ${OUT}/>
    <path d="M20 44 L44 44 L44 58 L20 58 Z" fill="${css('uiPanel')}" ${DET}/>
    <path d="M12 62 L88 62" ${OUT} fill="none"/>
    <path d="M12 78 L12 88 M88 78 L88 88" ${OUT} fill="none"/>`,

  kitchen: (p) => `
    <path d="M18 32 L82 32 L82 86 L18 86 Z" fill="${fill(p, 'wallCream')}" ${OUT}/>
    <circle cx="36" cy="48" r="8" fill="${css('roofRed')}" ${DET}/>
    <circle cx="64" cy="48" r="8" fill="${css('roofRed')}" ${DET}/>
    <path d="M28 66 L72 66 L72 82 L28 82 Z" fill="${css('glass')}" ${DET}/>
    <path d="M30 22 Q36 12 42 22 M58 22 Q64 12 70 22" ${DET} fill="none"/>`,

  bath: (p) => `
    <path d="M12 52 L88 52 L82 82 L18 82 Z" fill="${fill(p, 'wallBlue')}" ${OUT}/>
    <circle cx="34" cy="34" r="9" fill="${css('glass')}" ${DET}/>
    <circle cx="58" cy="26" r="7" fill="${css('glass')}" ${DET}/>
    <circle cx="70" cy="40" r="5" fill="${css('glass')}" ${DET}/>`,

  light: (p) => `
    <path d="M28 46 L50 16 L72 46 Z" fill="${fill(p, 'uiCoin')}" ${OUT}/>
    <path d="M50 46 L50 78" ${OUT} fill="none"/>
    <path d="M30 86 L70 86" ${OUT} fill="none" stroke-width="11"/>
    <path d="M18 56 L8 62 M82 56 L92 62" ${DET} fill="none"/>`,

  decor: (p) => `
    <path d="M18 18 L82 18 L82 70 L18 70 Z" fill="${fill(p, 'wallLilac')}" ${OUT}/>
    <path d="M26 60 L42 40 L54 54 L64 44 L74 60 Z" fill="${css('leaf')}" ${DET}/>
    <circle cx="64" cy="32" r="6" fill="${css('uiCoin')}"/>
    <path d="M34 70 L34 84 M66 70 L66 84" ${DET} fill="none"/>`,

  plant: (p) => `
    <path d="M50 88 L50 48" ${OUT} fill="none"/>
    <circle cx="50" cy="34" r="16" fill="${fill(p, 'flowerPink')}" ${OUT}/>
    <circle cx="50" cy="34" r="6" fill="${css('flowerYellow')}"/>
    <path d="M50 62 Q28 58 26 40 Q46 42 50 62 Z" fill="${css('leaf')}" ${DET}/>
    <path d="M50 70 Q72 66 74 48 Q54 50 50 70 Z" fill="${css('leaf')}" ${DET}/>`,

  tree: (p) => `
    <path d="M44 90 L44 58 L56 58 L56 90 Z" fill="${css('trunk')}" ${OUT}/>
    <circle cx="50" cy="38" r="26" fill="${fill(p, 'leaf')}" ${OUT}/>
    <circle cx="30" cy="50" r="16" fill="${fill(p, 'leafLight')}" ${OUT}/>
    <circle cx="70" cy="50" r="16" fill="${fill(p, 'leafDark')}" ${OUT}/>`,

  water: (p) => `
    <ellipse cx="50" cy="58" rx="38" ry="26" fill="${fill(p, 'water')}" ${OUT}/>
    <path d="M26 52 Q34 44 42 52 T58 52 T74 52" ${DET} fill="none" stroke="${css('waterDeep')}"/>
    <path d="M32 68 Q40 60 48 68 T64 68" ${DET} fill="none" stroke="${css('waterDeep')}"/>`,

  townProp: (p) => `
    <path d="M50 88 L50 34" ${OUT} fill="none" stroke-width="10"/>
    <path d="M34 34 L66 34 L58 14 L42 14 Z" fill="${fill(p, 'uiCoin')}" ${OUT}/>
    <path d="M30 88 L70 88" ${OUT} fill="none" stroke-width="11"/>`,

  venueFixture: (p) => `
    <path d="M12 46 L88 46 L88 86 L12 86 Z" fill="${fill(p, 'wallMint')}" ${OUT}/>
    <path d="M12 46 L88 46" ${OUT} fill="none"/>
    <circle cx="34" cy="30" r="12" fill="${css('iceStrawberry')}" ${OUT}/>
    <circle cx="62" cy="32" r="10" fill="${css('iceMint')}" ${OUT}/>`,

  toy: (p) => `
    <circle cx="50" cy="52" r="34" fill="${fill(p, 'flowerRed')}" ${OUT}/>
    <path d="M16 52 L84 52" ${OUT} fill="none"/>
    <path d="M50 18 Q68 52 50 86 Q32 52 50 18 Z" fill="${css('uiPanel')}" opacity="0.45"/>`,

  appliance: (p) => `
    <path d="M22 14 L78 14 L78 88 L22 88 Z" fill="${fill(p, 'metal')}" ${OUT}/>
    <path d="M22 40 L78 40" ${OUT} fill="none"/>
    <circle cx="50" cy="62" r="16" fill="${css('glass')}" ${OUT}/>
    <circle cx="34" cy="27" r="4" fill="${INK}"/>`,

  // -- food (venue order cards) ---------------------------------------------
  scoop: (p) => `
    <circle cx="50" cy="36" r="26" fill="${fill(p, 'iceStrawberry')}" ${OUT}/>
    <path d="M26 56 L74 56 L50 92 Z" fill="${css('cone')}" ${OUT}/>
    <path d="M36 64 L58 64 M42 74 L54 74" ${DET} fill="none" opacity="0.5"/>`,

  /*
   * ONE PORTION OF ANYTHING — a scoop of ice cream, a spoonful of pasta, a
   * ladle of soup. NO CONE.
   *
   * The middle slot of an order card is "how many", and it used to draw `scoop`,
   * which has a cone baked into it. Two things went wrong at once: the cone was
   * drawn twice on an ice cream card (once in the "what it's in" slot and again
   * under every portion), and a RESTAURANT card — a plate of pasta — showed a
   * plate next to a pink ice-cream cone. The base slot already says what the
   * food is in; this says how much and what colour, and nothing else.
   */
  portion: (p) => `
    <ellipse cx="50" cy="78" rx="30" ry="9" fill="${css('inkSoft')}" opacity="0.18"/>
    <circle cx="50" cy="48" r="30" fill="${fill(p, 'iceStrawberry')}" ${OUT}/>
    <path d="M32 34 A22 22 0 0 1 50 26" stroke="${css('flowerWhite')}" stroke-width="8"
          fill="none" stroke-linecap="round"/>`,

  cone: (p) => `
    <path d="M26 30 L74 30 L50 90 Z" fill="${fill(p, 'cone')}" ${OUT}/>
    <path d="M34 46 L62 46 M40 62 L58 62" ${DET} fill="none" opacity="0.5"/>`,

  /*
   * THE WHOLE ICE CREAM, NOT THE CONE STEP (added r3).
   *
   * `cone` above means one specific thing in the restaurant's order tickets —
   * the empty cone you pick up before you scoop — so it is a bare tan triangle,
   * correctly. Used as a SIGNPOST for the ice cream shop it read as "a dark
   * triangle" and named nothing. This is the shop's picture: two scoops, a
   * cherry, colour to the edges, still legible at 32px on a shrunk marker.
   */
  iceCream: (p) => `
    <path d="M30 52 L70 52 L50 94 Z" fill="${css('cone')}" ${OUT}/>
    <path d="M38 64 L60 64" ${DET} fill="none" opacity="0.45"/>
    <circle cx="38" cy="42" r="19" fill="${css('iceStrawberry')}" ${OUT}/>
    <circle cx="62" cy="42" r="19" fill="${fill(p, 'iceMint')}" ${OUT}/>
    <circle cx="50" cy="24" r="18" fill="${css('iceVanilla')}" ${OUT}/>
    <circle cx="50" cy="8" r="7" fill="${css('cherry')}" ${OUT}/>`,

  /*
   * A PLACE TO EAT (added r3). `plate` is an ellipse seen from above with a
   * lighter ellipse inside it — on a white signpost card that is a white
   * ellipse outline and nothing else. A plate with FOOD on it, between a fork
   * and a spoon, says restaurant without a word on it.
   */
  dinner: (p) => `
    <ellipse cx="50" cy="72" rx="42" ry="16" fill="${fill(p, 'plate')}" ${OUT}/>
    <path d="M18 66 A32 32 0 0 1 82 66 Z" fill="${css('iceStrawberry')}" ${OUT}/>
    <circle cx="34" cy="52" r="11" fill="${css('leaf')}" ${OUT}/>
    <circle cx="62" cy="46" r="13" fill="${css('uiCoin')}" ${OUT}/>`,

  cherry: (p) => `
    <path d="M50 22 Q64 34 68 54" ${DET} fill="none" stroke="${css('leafDark')}"/>
    <circle cx="66" cy="66" r="18" fill="${fill(p, 'cherry')}" ${OUT}/>
    <path d="M50 22 Q36 34 32 54" ${DET} fill="none" stroke="${css('leafDark')}"/>
    <circle cx="30" cy="66" r="14" fill="${fill(p, 'cherry')}" ${OUT}/>`,

  plate: (p) => `
    <ellipse cx="50" cy="58" rx="40" ry="24" fill="${fill(p, 'plate')}" ${OUT}/>
    <ellipse cx="50" cy="56" rx="24" ry="13" fill="${css('uiPanelAlt')}" ${DET}/>`,

  /* A tub you put ice cream IN. `cup` used to fall through to `plate`, which is
     an ellipse seen edge-on — beside a cone it read as a plain oval, and a kid
     had no way to tell that the middle card in a row of three was asking for the
     same kind of thing as the two either side. */
  cup: (p) => `
    <path d="M24 34 L76 34 L68 88 L32 88 Z" fill="${fill(p, 'plate')}" ${OUT}/>
    <ellipse cx="50" cy="34" rx="26" ry="10" fill="${css('uiPanelAlt')}" ${OUT}/>
    <path d="M30 56 L70 56" ${DET} fill="none" opacity="0.45"/>`,

  /* A ladle pouring sauce. The restaurant's toppings are sauce and cheese, and
     a sauce drawn as a generic sparkle told a kid nothing about what it was. */
  sauce: (p) => `
    <path d="M30 20 L70 20 L62 52 L38 52 Z" fill="${fill(p, 'flowerRed')}" ${OUT}/>
    <path d="M50 52 Q50 68 42 78 Q50 92 58 78 Q50 68 50 52 Z" fill="${fill(p, 'flowerRed')}" ${DET}/>
    <ellipse cx="50" cy="20" rx="20" ry="8" fill="${css('uiPanelAlt')}" ${OUT}/>`,

  cake: (p) => `
    <path d="M20 52 L80 52 L76 86 L24 86 Z" fill="${fill(p, 'wallPink')}" ${OUT}/>
    <path d="M20 52 Q32 42 44 52 T68 52 T80 52" ${OUT} fill="${css('iceVanilla')}"/>
    <path d="M50 40 L50 22" ${DET} fill="none"/>
    <circle cx="50" cy="18" r="6" fill="${css('uiCoin')}"/>`,

  // -- monster creator ------------------------------------------------------
  shapeBlob: (p) => `
    <path d="M50 14 C80 14 90 40 88 60 C86 82 70 92 50 92 C30 92 14 82 12 60 C10 40 20 14 50 14 Z"
          fill="${fill(p, 'monsterTeal')}" ${OUT}/>`,
  shapeTall: (p) => `
    <path d="M50 8 C70 8 76 26 76 48 L76 76 C76 88 64 94 50 94 C36 94 24 88 24 76 L24 48 C24 26 30 8 50 8 Z"
          fill="${fill(p, 'monsterTeal')}" ${OUT}/>`,
  shapeRound: (p) => `<circle cx="50" cy="52" r="40" fill="${fill(p, 'monsterTeal')}" ${OUT}/>`,
  shapeChunky: (p) => `
    <path d="M18 24 L82 24 C88 24 90 30 90 38 L90 74 C90 84 84 90 74 90 L26 90 C16 90 10 84 10 74 L10 38 C10 30 12 24 18 24 Z"
          fill="${fill(p, 'monsterTeal')}" ${OUT}/>`,

  eyesTwo: () => `
    <circle cx="34" cy="50" r="16" fill="${css('eyeWhite')}" ${OUT}/>
    <circle cx="66" cy="50" r="16" fill="${css('eyeWhite')}" ${OUT}/>
    <circle cx="37" cy="53" r="7" fill="${css('pupil')}"/>
    <circle cx="69" cy="53" r="7" fill="${css('pupil')}"/>`,
  eyesOne: () => `
    <circle cx="50" cy="50" r="26" fill="${css('eyeWhite')}" ${OUT}/>
    <circle cx="54" cy="54" r="11" fill="${css('pupil')}"/>`,
  eyesThree: () => `
    <circle cx="26" cy="56" r="13" fill="${css('eyeWhite')}" ${OUT}/>
    <circle cx="50" cy="34" r="13" fill="${css('eyeWhite')}" ${OUT}/>
    <circle cx="74" cy="56" r="13" fill="${css('eyeWhite')}" ${OUT}/>
    <circle cx="28" cy="58" r="6" fill="${css('pupil')}"/>
    <circle cx="52" cy="36" r="6" fill="${css('pupil')}"/>
    <circle cx="76" cy="58" r="6" fill="${css('pupil')}"/>`,
  eyesSleepy: () => `
    <path d="M20 50 Q34 66 48 50" ${OUT} fill="none" stroke-width="9"/>
    <path d="M54 50 Q68 66 82 50" ${OUT} fill="none" stroke-width="9"/>`,

  hornsNone: () => `<path d="M22 60 Q50 40 78 60" ${OUT} fill="none" opacity="0.35"/>`,
  hornsNubs: (p) => `
    <path d="M30 78 L30 52" ${OUT} fill="none" stroke-width="12"/>
    <path d="M70 78 L70 52" ${OUT} fill="none" stroke-width="12"/>
    <circle cx="30" cy="46" r="12" fill="${fill(p, 'horn')}" ${OUT}/>
    <circle cx="70" cy="46" r="12" fill="${fill(p, 'horn')}" ${OUT}/>`,
  hornsCurved: (p) => `
    <path d="M28 82 C10 60 20 30 44 26" stroke="${fill(p, 'horn')}" stroke-width="15" fill="none" stroke-linecap="round"/>
    <path d="M72 82 C90 60 80 30 56 26" stroke="${fill(p, 'horn')}" stroke-width="15" fill="none" stroke-linecap="round"/>
    <path d="M28 82 C10 60 20 30 44 26 M72 82 C90 60 80 30 56 26" ${DET} fill="none"/>`,
  hornsAntlers: (p) => `
    <path d="M36 86 L30 50 L12 34 M30 62 L14 56 M64 86 L70 50 L88 34 M70 62 L86 56"
          stroke="${fill(p, 'horn')}" stroke-width="11" fill="none" stroke-linecap="round"/>
    <path d="M36 86 L30 50 L12 34 M30 62 L14 56 M64 86 L70 50 L88 34 M70 62 L86 56" ${DET} fill="none" opacity="0.6"/>`,
  hornsAntennae: (p) => `
    <path d="M38 88 C30 60 24 40 20 28" ${OUT} fill="none"/>
    <path d="M62 88 C70 60 76 40 80 28" ${OUT} fill="none"/>
    <circle cx="18" cy="22" r="11" fill="${fill(p, 'flowerPink')}" ${OUT}/>
    <circle cx="82" cy="22" r="11" fill="${fill(p, 'flowerPink')}" ${OUT}/>`,

  patternPlain: (p) => `<circle cx="50" cy="50" r="38" fill="${fill(p, 'monsterLilac')}" ${OUT}/>`,
  patternSpots: (p) => `
    <circle cx="50" cy="50" r="38" fill="${fill(p, 'monsterLilac')}" ${OUT}/>
    <circle cx="36" cy="38" r="9" fill="${css('monsterBelly')}"/>
    <circle cx="64" cy="46" r="7" fill="${css('monsterBelly')}"/>
    <circle cx="46" cy="66" r="8" fill="${css('monsterBelly')}"/>`,
  patternStripes: (p) => `
    <circle cx="50" cy="50" r="38" fill="${fill(p, 'monsterLilac')}" ${OUT}/>
    <path d="M26 30 L34 76 M50 18 L50 82 M74 30 L66 76" stroke="${css('monsterBelly')}"
          stroke-width="10" fill="none" stroke-linecap="round"/>`,
  patternBelly: (p) => `
    <circle cx="50" cy="50" r="38" fill="${fill(p, 'monsterLilac')}" ${OUT}/>
    <ellipse cx="50" cy="60" rx="24" ry="20" fill="${css('monsterBelly')}" ${DET}/>`,
  patternStar: (p) => `
    <circle cx="50" cy="50" r="38" fill="${fill(p, 'monsterLilac')}" ${OUT}/>
    <path d="M50 24 L58 44 L80 46 L64 60 L69 82 L50 70 L31 82 L36 60 L20 46 L42 44 Z"
          fill="${css('monsterBelly')}"/>`,

  // -- misc -----------------------------------------------------------------
  house: (p) => `
    <path d="M50 12 L92 48 L82 48 L82 88 L18 88 L18 48 L8 48 Z" fill="${fill(p, 'wallCream')}" ${OUT}/>
    <path d="M50 12 L92 48 L8 48 Z" fill="${css('roofRed')}" ${OUT}/>
    <path d="M40 58 L60 58 L60 88 L40 88 Z" fill="${css('doorWood')}" ${OUT}/>`,
  blob: (p) => `
    <path d="M50 12 C78 12 90 34 88 56 C86 80 68 90 50 90 C32 90 14 80 12 56 C10 34 22 12 50 12 Z"
          fill="${fill(p, 'monsterCream')}" ${OUT}/>
    <circle cx="40" cy="48" r="5" fill="${INK}"/>
    <circle cx="62" cy="48" r="5" fill="${INK}"/>
    <path d="M40 66 Q51 74 62 66" ${DET} fill="none"/>`,
}

// ---------------------------------------------------------------------------
// api
// ---------------------------------------------------------------------------

export type IconName = string

export function hasIcon(name: string): boolean {
  return name in ICONS
}

export function iconNames(): string[] {
  return Object.keys(ICONS)
}

/** Raw SVG markup for an icon. Unknown names fall back to a friendly blob. */
export function iconSvg(name: IconName, pen: Pen = {}): string {
  const draw = ICONS[name] ?? ICONS['blob']
  const body = draw === undefined ? '' : draw(pen)
  return `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true">${body}</svg>`
}

/**
 * An icon as a DOM element sized in CSS px.
 *
 * WHY THE SIZE IS SET THREE TIMES, INLINE — this is the fix for the reported
 * screen-filling coin.
 *
 * An `<svg>` carrying only a `viewBox` has an intrinsic RATIO but no intrinsic
 * SIZE, so a browser sizes it to 100% of its containing block. Two stylesheet
 * rules were the only thing holding it to 60 px: `.mt-ico { display:block }`
 * (without which an inline span IGNORES its own width and height) and
 * `.mt-ico > svg { width:100%; height:100% }`. `ui.css` is a JS-injected module
 * stylesheet — in dev it lands after first paint, and it is removed and
 * re-added on every HMR update. For the frames where it is not applied, the
 * span collapses to inline, the svg claims the full width of the HUD, and the
 * coin in the purse becomes an ~830 px picture across the middle of the screen
 * while every absolutely-positioned widget around it loses its position. That is
 * precisely the intermittent artifact that was flagged, and it is unreproducible
 * on demand because it depends on when the stylesheet lands.
 *
 * Setting `display`, `width`/`height` and the svg's own attributes inline makes
 * an icon exactly `sizePx` with NO stylesheet at all. The values are identical
 * to what `ui.css` says, so nothing changes visually; it just cannot be lost.
 * `tools/ui-check.mjs` now also fails any frame containing a HUD element wider
 * than 30% of the viewport, so a regression here is caught rather than reported.
 */
export function iconEl(name: IconName, sizePx: number, pen: Pen = {}): HTMLSpanElement {
  const span = document.createElement('span')
  span.className = 'mt-ico'
  span.style.display = 'block'
  span.style.flex = 'none'
  span.style.width = `${sizePx}px`
  span.style.height = `${sizePx}px`
  span.innerHTML = iconSvg(name, pen)
  const svg = span.firstElementChild
  if (svg !== null) {
    svg.setAttribute('width', '100%')
    svg.setAttribute('height', '100%')
  }
  return span
}

/** A data: URL, for CSS backgrounds and the photo-mode composite. */
export function iconDataUrl(name: IconName, pen: Pen = {}): string {
  return `data:image/svg+xml;utf8,${encodeURIComponent(iconSvg(name, pen))}`
}

/** Category -> icon. Used by the drawer and the shop. DATA, not logic. */
export const CATEGORY_ICON: Record<string, string> = {
  block: 'block',
  floor: 'floor',
  wall: 'wall',
  roof: 'roof',
  door: 'door',
  window: 'window',
  stair: 'stair',
  path: 'path',
  fence: 'fence',
  furniture: 'furniture',
  bed: 'bed',
  kitchen: 'kitchen',
  bath: 'bath',
  light: 'light',
  decor: 'decor',
  plant: 'plant',
  tree: 'tree',
  water: 'water',
  townProp: 'townProp',
  venueFixture: 'venueFixture',
  toy: 'toy',
  appliance: 'appliance',
}
